import type { Deal } from '@/api/gen'
import { useDealAugustPromoDealsRetrieve } from '@/api/gen'
import { DataTable } from '@/components/data-table'
import { tableQueryState } from '@/components/table-query-state'
import { useAuth } from '@/hooks/use-auth'
import type { TicketStatusRow } from '@/pages/promotion/hooks/use-tickets-status-table-cols'
import { useTicketsStatusCols } from '@/pages/promotion/hooks/use-tickets-status-table-cols'
import { useUserPromoStatus } from '@/pages/promotion/hooks/use-user-promo-status'

export const TicketsStatusTable = () => {
  const { isAuthorized, isInitialized } = useAuth()

  const { data, error, isError, isFetching, refetch } = useDealAugustPromoDealsRetrieve({
    query: {
      select(data) {
        return {
          ...data,
          deals: data.deals.reduce<Deal[]>((acc, deal) => {
            if (!acc.some((d) => d.poker_site_slug === deal.poker_site_slug)) {
              acc.push(deal)
            }

            return acc
          }, []),
        }
      },
    },
  })

  const { loading: promoStatusLoading, tickets } = useUserPromoStatus()

  const columns = useTicketsStatusCols()

  // Annotated so the row type comes from the COLUMNS' contract rather than being inferred
  // from the literal built here — otherwise the two disagree on the API's looser field types.
  const rows: TicketStatusRow[] =
    data?.deals.map((deal) => ({
      sites: deal.poker_site_slug,
      id: deal.poker_site_slug,
      verified: tickets?.sites[deal.poker_site_slug]?.verified ?? false,
      prize_earned: tickets?.sites[deal.poker_site_slug]?.prize_earned ?? '',
      gto_wizard_subscription: tickets?.gto_wizard_subscription.highest_tier_aug_2025 ?? null,
      logo: deal.poker_site_local_s3_url,
      name: deal.poker_site_name,
    })) ?? []

  return (
    <DataTable
      // A floor height so the card keeps its shape while the deals load, rather than
      // collapsing to a header and jumping when the rows arrive.
      className="min-h-[210px]"
      columns={columns}
      data={rows}
      // The second and last table off the DS default. Its room cell renders `SiteLogo`, whose
      // no-logo placeholder is a 40px `PhotoIcon` inside `py-1` = 48px on desktop, which a 40px
      // `standard` row clips top and bottom. `logo` is nullable, so that is a live path.
      density="comfortable"
      getRowId={(row) => row.id}
      // Both queries run with default options — no `staleTime`, `refetchOnWindowFocus` on — so a
      // tab refocus refetches while the cached rows are still mounted. Reporting that as
      // `loading` swaps them for a skeleton mid-read, which is what this split prevents.
      {...tableQueryState({
        // BOTH queries, because `isFetching` below covers both: the deals resolve first, so
        // counting only them reports the promo-status first load as a refetch and the table paints
        // a signed-in user's own columns from the row literal's defaults — "No" with a red cross
        // where a verified account belongs — before flipping.
        error,
        hasData: data !== undefined && (!isAuthorized || tickets !== null),
        isError,
        isFetching: isFetching || promoStatusLoading,
        isInitialized,
        refetch,
      })}
    />
  )
}
